import { Mail, Linkedin } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScrollReveal } from './ScrollReveal';
import { ScrapbookSticker } from './scrapbook/ScrapbookSticker';

export function ContactSection() {
  return (
    <section id="contact" className="relative min-h-screen flex items-center justify-center py-24 px-6 overflow-hidden">
      {/* Ocean floor base */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/40 to-slate-950/90" />

      {/* Subtle blue ambient light from above (filtered sunlight) */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[1400px] h-[1400px] pointer-events-none opacity-5 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(100,150,180,0.2) 0%, transparent 60%)',
        }}
      />

      {/* Ocean floor silhouettes - rocks and coral */}
      <div className="absolute bottom-0 left-0 right-0 h-[40vh] pointer-events-none overflow-hidden">
        {/* Large rock formation - left side */}
        <svg
          className="absolute bottom-0 left-0 w-[30%] h-[60%] opacity-40"
          viewBox="0 0 300 400"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 400 L0 280 Q20 260 40 250 Q60 240 80 220 Q100 200 120 190 Q140 180 150 160 Q160 140 180 130 Q200 120 220 140 Q240 160 260 180 L280 200 L300 240 L300 400 Z"
            fill="rgba(30, 58, 78, 0.7)"
          />
          <path
            d="M0 400 L0 320 Q30 300 60 290 Q90 280 110 260 Q130 240 150 250 Q170 260 190 280 L210 300 L230 340 L250 380 L300 400 Z"
            fill="rgba(20, 45, 60, 0.5)"
          />
        </svg>


        {/* Rock cluster - center */}
        <svg
          className="absolute bottom-0 left-[35%] w-[25%] h-[45%] opacity-45"
          viewBox="0 0 250 300"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="80" cy="260" rx="70" ry="50" fill="rgba(25, 50, 65, 0.7)" />
          <ellipse cx="150" cy="240" rx="60" ry="55" fill="rgba(30, 55, 70, 0.6)" />
          <ellipse cx="120" cy="220" rx="50" ry="40" fill="rgba(35, 60, 75, 0.5)" />
          <path
            d="M40 300 Q60 280 80 270 Q100 260 120 250 Q140 240 160 245 Q180 250 200 260 L220 280 L250 300 Z"
            fill="rgba(20, 45, 60, 0.8)"
          />
        </svg>

        {/* Tall kelp/seaweed - center-right */}
        <svg
          className="absolute bottom-0 left-[55%] w-[15%] h-[70%] opacity-35"
          viewBox="0 0 150 500"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            d="M60 500 Q55 450 60 400 Q65 350 55 300 Q50 250 60 200 Q70 150 60 100 L55 50"
            stroke="rgba(30, 60, 80, 0.6)"
            strokeWidth="6"
            fill="none"
            animate={{
              d: [
                "M60 500 Q55 450 60 400 Q65 350 55 300 Q50 250 60 200 Q70 150 60 100 L55 50",
                "M60 500 Q65 450 55 400 Q50 350 60 300 Q65 250 55 200 Q50 150 60 100 L65 50",
                "M60 500 Q55 450 60 400 Q65 350 55 300 Q50 250 60 200 Q70 150 60 100 L55 50",
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.path
            d="M90 500 Q85 450 90 400 Q95 350 85 300 Q80 250 90 200 L95 150"
            stroke="rgba(25, 55, 75, 0.5)"
            strokeWidth="5"
            fill="none"
            animate={{
              d: [
                "M90 500 Q85 450 90 400 Q95 350 85 300 Q80 250 90 200 L95 150",
                "M90 500 Q95 450 85 400 Q80 350 90 300 Q95 250 85 200 L80 150",
                "M90 500 Q85 450 90 400 Q95 350 85 300 Q80 250 90 200 L95 150",
              ]
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
        </svg>

        {/* Tall kelp/seaweed - center-left */}
        <svg
          className="absolute bottom-0 left-[26%] w-[15%] h-[70%] opacity-35"
          viewBox="0 0 150 500"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            d="M60 500 Q65 450 60 400 Q55 350 65 300 Q70 250 60 200 Q50 150 60 100 L65 50"
            stroke="rgba(30, 60, 80, 0.6)"
            strokeWidth="6"
            fill="none"
            animate={{
              d: [
                "M60 500 Q65 450 60 400 Q55 350 65 300 Q70 250 60 200 Q50 150 60 100 L65 50",
                "M60 500 Q55 450 65 400 Q70 350 60 300 Q55 250 65 200 Q70 150 60 100 L55 50",
                "M60 500 Q65 450 60 400 Q55 350 65 300 Q70 250 60 200 Q50 150 60 100 L65 50",
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          />
          <motion.path
            d="M90 500 Q95 450 90 400 Q85 350 95 300 Q100 250 90 200 L85 150"
            stroke="rgba(25, 55, 75, 0.5)"
            strokeWidth="5"
            fill="none"
            animate={{
              d: [
                "M90 500 Q95 450 90 400 Q85 350 95 300 Q100 250 90 200 L85 150",
                "M90 500 Q85 450 95 400 Q100 350 90 300 Q85 250 95 200 L100 150",
                "M90 500 Q95 450 90 400 Q85 350 95 300 Q100 250 90 200 L85 150",
              ]
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5
            }}
          />
        </svg>


        {/* Large rock - far right */}
        <svg
          className="absolute bottom-0 right-0 w-[25%] h-[50%] opacity-40"
          viewBox="0 0 250 350"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 350 L0 280 Q20 260 50 240 Q80 220 110 210 Q140 200 170 220 Q200 240 220 260 L240 290 L250 350 Z"
            fill="rgba(28, 55, 72, 0.7)"
          />
          <ellipse cx="120" cy="280" rx="80" ry="60" fill="rgba(20, 45, 60, 0.5)" />
        </svg>

        {/* Coral reef stickers - properly positioned on seafloor */}

        {/* Coral 1 - left side, partially behind rock formation */}
        <div className="absolute bottom-0 left-0 pointer-events-none hidden lg:block" style={{ mixBlendMode: 'darken' }}>
          <ScrapbookSticker
            src="/images/sticker8.2.png"
            size={240}
            rotation={-2}
            depth="deep"
            opacity={0.65}
            removeBackground={true}
          />
        </div>

        {/* Coral 2 - center-left, lower position */}
        <div className="absolute bottom-[-40px] left-[12%] pointer-events-none hidden lg:block" style={{ mixBlendMode: 'darken' }}>
          <ScrapbookSticker
            src="/images/sticker9.2.png"
            size={220}
            rotation={3}
            depth="deep"
            opacity={0.9}
            removeBackground={true}
          />
        </div>

        {/* Coral 3 - rightmost side, inverted */}
        <div className="absolute bottom-0 right-0 pointer-events-none hidden lg:block" style={{ mixBlendMode: 'darken', transform: 'scaleX(-1) rotate(4deg)' }}>
          <ScrapbookSticker
            src="/images/sticker8.2.png"
            size={260}
            rotation={0}
            depth="deep"
            opacity={0.68}
            removeBackground={true}
          />
        </div>

        {/* Coral 4 - right side, lower position, inverted */}
        <div className="absolute bottom-[-40px] right-[12%] pointer-events-none hidden lg:block" style={{ mixBlendMode: 'darken', transform: 'scaleX(-1) rotate(-3deg)' }}>
          <ScrapbookSticker
            src="/images/sticker9.2.png"
            size={220}
            rotation={0}
            depth="deep"
            opacity={0.9}
            removeBackground={true}
          />
        </div>

        {/* Ocean floor ground */}
        <div className="absolute bottom-0 left-0 right-0 h-[5%] bg-gradient-to-t from-slate-950/80 to-transparent" />
      </div>

      {/* Subtle bioluminescent particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(10)].map((_, i) => {
          const left = Math.random() * 100;
          const bottom = Math.random() * 40;
          const size = 1 + Math.random() * 2;
          const delay = Math.random() * 3;

          return (
            <motion.div
              key={i}
              className="absolute rounded-full bg-cyan-300/30"
              style={{
                left: `${left}%`,
                bottom: `${bottom}%`,
                width: `${size}px`,
                height: `${size}px`,
                filter: 'blur(1px)',
              }}
              animate={{
                opacity: [0.2, 0.5, 0.2],
                scale: [1, 1.3, 1],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      {/* Rising bubbles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => {
          const left = 10 + Math.random() * 80;
          const size = 4 + Math.random() * 8;
          const duration = 8 + Math.random() * 6;
          const delay = Math.random() * 8;
          const drift = -20 + Math.random() * 40;

          return (
            <motion.div
              key={`bubble-${i}`}
              className="absolute rounded-full"
              style={{
                left: `${left}%`,
                bottom: '-10%',
                width: `${size}px`,
                height: `${size}px`,
                background: 'radial-gradient(circle at 30% 30%, rgba(200, 230, 255, 0.4), rgba(150, 200, 230, 0.2))',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: 'inset 0 0 8px rgba(255, 255, 255, 0.3)',
              }}
              animate={{
                y: [0, -window.innerHeight - 100],
                x: [0, drift, -drift, 0],
                opacity: [0, 0.6, 0.6, 0],
              }}
              transition={{
                duration,
                delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      <div className="max-w-4xl mx-auto relative z-10 text-center">
        {/* Subtle warm-gold point - distant memory of the surface */}
        <motion.div
          className="absolute -top-32 right-12 w-2 h-2 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(240,184,85,0.15) 0%, transparent 70%)',
            boxShadow: '0 0 8px rgba(240,184,85,0.08)',
          }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Label */}
        <ScrollReveal className="mb-editorial-md">
          <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider justify-center">
            <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
            <span>Let's Connect</span>
          </div>
        </ScrollReveal>

        {/* Large editorial statement - final moment */}
        <div className="mb-editorial-md">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="font-bold text-slate-50"
            style={{
              fontSize: 'clamp(4rem, 8vw, 8rem)',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Get In Touch
          </motion.h2>
        </div>

        <ScrollReveal delay={0.3} className="mb-editorial-lg">
          <p className="text-editorial-body text-slate-200/70 max-w-2xl mx-auto leading-relaxed">
            I'm always interested in new opportunities and conversations about design, research, and technology.
          </p>
        </ScrollReveal>

        {/* Contact links - icons only */}
        <ScrollReveal delay={0.5}>
          <div className="flex items-center justify-center gap-6">
            <a
              href="mailto:jjulianmatos@gmail.com"
              className="p-3 bg-slate-800/50 rounded-lg hover:bg-cyan-500/30 border border-cyan-400/30 transition-all duration-300 text-cyan-400 backdrop-blur-sm hover:scale-110"
            >
              <Mail className="w-6 h-6" />
            </a>

            <a
              href="https://www.linkedin.com/in/jmatos-dev/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-slate-800/50 rounded-lg hover:bg-cyan-500/30 border border-cyan-400/30 transition-all duration-300 text-cyan-400 backdrop-blur-sm hover:scale-110"
            >
              <Linkedin className="w-6 h-6" />
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
